import { ErrorBoundaryFatal } from "@/components/errors/ErrorBoundaryFatal";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { MainLayoutFallback } from "./layouts/MainLayoutFallback";
import Index from "./pages/Index";
import "./App.css";
import { trackPageView } from "./utils/analytics";
import { queryClient } from "./queries/client";

import { PlanOnboardingContinuation } from "./components/plans/PlanOnboardingContinuation";
const Plans = lazy(() => import("./pages/Plans"));

const LocalShops = lazy(() => import("./pages/LocalShops"));
const SellerLocalShop = lazy(() => import("./pages/SellerLocalShop"));
const LocalShopReview = lazy(() => import("./pages/LocalShopReview"));
const LiveShows = lazy(() => import("./pages/LiveShows"));
const HostLiveShows = lazy(() => import("./pages/HostLiveShows"));
const LlamaLounge = lazy(() => import("./pages/LlamaLounge"));
const CollectorTools = lazy(() => import("./pages/CollectorTools"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));

const CollectorDiscovery = lazy(() => import("./pages/CollectorDiscovery"));
const MarketplaceForYou = lazy(() => import("./pages/MarketplaceForYou"));
const CollectorShop = lazy(() => import("./pages/CollectorShop"));
const ShopReviewQueue = lazy(() => import("./pages/ShopReviewQueue"));
const EditCollectorShop = lazy(() => import("./pages/EditCollectorShop"));
const Market = lazy(() => import("./pages/Market"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MarketplaceListingDetail = lazy(
  () => import("./pages/MarketplaceListingDetail"),
);
const BuyerPurchases = lazy(() => import("./pages/BuyerPurchases"));
const Cards = lazy(() => import("./pages/Cards"));
const VirtualBinder = lazy(() => import("./pages/VirtualBinder"));
const CollectionCoach = lazy(() => import("./pages/CollectionCoach"));
const AIGrader = lazy(() => import("./pages/AIGrader"));
const GradingPhoneCapture = lazy(() => import("./pages/GradingPhoneCapture"));
const DatasetReview = lazy(() => import("./pages/DatasetReview"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const NewsUpdates = lazy(() => import("./pages/NewsUpdates"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Scan = lazy(() => import("./pages/Scan"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Alerts = lazy(() => import("./pages/Alerts"));
const SellerPro = lazy(() => import("./pages/SellerPro"));
const SellerCenter = lazy(() => import("./pages/SellerCenter"));
const OffersCollections = lazy(() => import("./pages/OffersCollections"));
const CreateListing = lazy(() => import("./pages/CreateListing"));
const MarketplaceCardCapture = lazy(
  () => import("./pages/MarketplaceCardCapture"),
);
const SellerListings = lazy(() => import("./pages/SellerListings"));
const SharedPage = lazy(() => import("./pages/Shared"));
const NotFound = lazy(() => import("./pages/NotFound"));
const YouTubeAuthor = lazy(() => import("./pages/YouTubeAuthor"));
const SetDetails = lazy(() => import("./pages/SetDetails"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Sets = lazy(() => import("./pages/Sets"));
const AuthConfirmation = lazy(() => import("./pages/AuthConfirmation"));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname + window.location.search);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ErrorBoundaryFatal>
      <QueryClientProvider client={queryClient}>
        <SettingsProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <PlanOnboardingContinuation />
                <Suspense fallback={<MainLayoutFallback />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/plans" element={<Plans />} />
                    <Route path="/llama-lounge" element={<LlamaLounge />} />
                    <Route path="/live-shows" element={<LiveShows />} />
                    <Route path="/live-shows/:id" element={<LiveShows />} />
                    <Route path="/seller/shows" element={<HostLiveShows />} />
                    <Route
                      path="/ai-tools"
                      element={<CollectorTools area="ai" />}
                    />
                    <Route
                      path="/watchlists"
                      element={<CollectorTools area="watch" />}
                    />
                    <Route path="/profile" element={<ProfileSettings />} />
                    <Route path="/market" element={<Market />} />
                    <Route path="/marketplace" element={<Marketplace />} />
                    <Route
                      path="/marketplace/local-shops"
                      element={<LocalShops />}
                    />
                    <Route
                      path="/marketplace/local-shops/:id"
                      element={<LocalShops />}
                    />
                    <Route
                      path="/seller/local-shop"
                      element={<SellerLocalShop />}
                    />
                    <Route
                      path="/seller/local-shop-review"
                      element={<LocalShopReview />}
                    />
                    <Route path="/seller/pro" element={<SellerPro />} />
                    <Route
                      path="/marketplace/offers"
                      element={<OffersCollections />}
                    />
                    <Route
                      path="/marketplace/for-you"
                      element={<MarketplaceForYou />}
                    />
                    <Route
                      path="/marketplace/discover"
                      element={<CollectorDiscovery />}
                    />
                    <Route
                      path="/marketplace/shops/:shopId"
                      element={<CollectorShop />}
                    />
                    <Route
                      path="/seller/shop-review"
                      element={<ShopReviewQueue />}
                    />
                    <Route
                      path="/seller/shop"
                      element={<EditCollectorShop />}
                    />
                    <Route
                      path="/marketplace/listings/:listingId"
                      element={<MarketplaceListingDetail />}
                    />
                    <Route path="/purchases" element={<BuyerPurchases />} />
                    <Route path="/sets/:id" element={<SetDetails />} />
                    <Route path="/cards" element={<Cards />} />
                    <Route path="/sets" element={<Sets />} />
                    <Route path="/virtual-binder" element={<VirtualBinder />} />
                    <Route
                      path="/collection-coach"
                      element={<CollectionCoach />}
                    />
                    <Route path="/watchlist" element={<Watchlist />} />
                    <Route path="/news" element={<NewsUpdates />} />
                    <Route path="/news/:slug" element={<NewsArticle />} />
                    <Route path="/scan" element={<Scan />} />
                    <Route path="/ai-grader" element={<AIGrader />} />
                    <Route
                      path="/ai-grader/capture/:sessionId"
                      element={<GradingPhoneCapture />}
                    />
                    <Route
                      path="/ai-grader/dataset-review"
                      element={<DatasetReview />}
                    />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/alerts" element={<Alerts />} />
                    <Route path="/deal-hunter" element={<Alerts />} />
                    <Route path="/seller" element={<SellerCenter />} />
                    <Route
                      path="/seller/listings"
                      element={<SellerListings />}
                    />
                    <Route
                      path="/seller/listings/new"
                      element={<CreateListing />}
                    />
                    <Route
                      path="/marketplace/capture/:verificationId"
                      element={<MarketplaceCardCapture />}
                    />
                    <Route path="/shared/:userId" element={<SharedPage />} />
                    <Route path="/youtube/:id" element={<YouTubeAuthor />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route
                      path="/auth/confirm"
                      element={<AuthConfirmation />}
                    />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </SettingsProvider>
      </QueryClientProvider>
    </ErrorBoundaryFatal>
  );
}

export default App;
